import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useLocation, Link } from 'react-router-dom';
import { propertyData } from '../mockData';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { LogIn, LogOut, User, CheckCircle } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { trackContactSubmit } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// Translation strings for Google auth
const authTranslations = {
  en: {
    signInPrompt: 'Sign in with Google to send an inquiry',
    signInButton: 'Sign in with Google',
    signedInAs: 'Signed in as',
    signOut: 'Sign out',
    phoneOptional: 'Phone (optional)',
    yourMessage: 'Your message',
    privacyConsent: 'I agree to the',
    privacyPolicy: 'Privacy Policy',
    privacyRequired: 'Please accept the Privacy Policy to continue'
  },
  gr: {
    signInPrompt: 'Συνδεθείτε με Google για να στείλετε ερώτημα',
    signInButton: 'Σύνδεση με Google',
    signedInAs: 'Συνδεδεμένος ως',
    signOut: 'Αποσύνδεση',
    phoneOptional: 'Τηλέφωνο (προαιρετικό)',
    yourMessage: 'Το μήνυμά σας',
    privacyConsent: 'Αποδέχομαι την',
    privacyPolicy: 'Πολιτική Απορρήτου',
    privacyRequired: 'Παρακαλώ αποδεχτείτε την Πολιτική Απορρήτου'
  },
  de: {
    signInPrompt: 'Mit Google anmelden um eine Anfrage zu senden',
    signInButton: 'Mit Google anmelden',
    signedInAs: 'Angemeldet als',
    signOut: 'Abmelden',
    phoneOptional: 'Telefon (optional)',
    yourMessage: 'Ihre Nachricht',
    privacyConsent: 'Ich stimme der',
    privacyPolicy: 'Datenschutzerklärung',
    privacyRequired: 'Bitte akzeptieren Sie die Datenschutzerklärung'
  },
  he: {
    signInPrompt: 'התחבר עם Google כדי לשלוח פנייה',
    signInButton: 'התחבר עם Google',
    signedInAs: 'מחובר כ',
    signOut: 'התנתק',
    phoneOptional: 'טלפון (אופציונלי)',
    yourMessage: 'ההודעה שלך',
    privacyConsent: 'אני מסכים ל',
    privacyPolicy: 'מדיניות הפרטיות',
    privacyRequired: 'אנא אשר את מדיניות הפרטיות'
  },
  fr: {
    signInPrompt: 'Connectez-vous avec Google pour envoyer une demande',
    signInButton: 'Se connecter avec Google',
    signedInAs: 'Connecté en tant que',
    signOut: 'Se déconnecter',
    phoneOptional: 'Téléphone (optionnel)',
    yourMessage: 'Votre message',
    privacyConsent: 'J\'accepte la',
    privacyPolicy: 'Politique de Confidentialité',
    privacyRequired: 'Veuillez accepter la Politique de Confidentialité'
  },
  zh: {
    signInPrompt: '使用Google登录以发送询问',
    signInButton: '使用Google登录',
    signedInAs: '已登录为',
    signOut: '退出',
    phoneOptional: '电话（可选）',
    yourMessage: '您的留言',
    privacyConsent: '我同意',
    privacyPolicy: '隐私政策',
    privacyRequired: '请接受隐私政策'
  },
  ar: {
    signInPrompt: 'سجل الدخول باستخدام Google لإرسال استفسار',
    signInButton: 'تسجيل الدخول باستخدام Google',
    signedInAs: 'مسجل الدخول كـ',
    signOut: 'تسجيل الخروج',
    phoneOptional: 'الهاتف (اختياري)',
    yourMessage: 'رسالتك',
    privacyConsent: 'أوافق على',
    privacyPolicy: 'سياسة الخصوصية',
    privacyRequired: 'يرجى قبول سياسة الخصوصية'
  },
  ru: {
    signInPrompt: 'Войдите через Google, чтобы отправить запрос',
    signInButton: 'Войти через Google',
    signedInAs: 'Вы вошли как',
    signOut: 'Выйти',
    phoneOptional: 'Телефон (необязательно)',
    yourMessage: 'Ваше сообщение',
    privacyConsent: 'Я согласен с',
    privacyPolicy: 'Политикой конфиденциальности',
    privacyRequired: 'Пожалуйста, примите Политику конфиденциальности'
  }
};

const Contact = () => {
  const { language } = useLanguage();
  const { user, loading, loginWithGoogle, logout, updateUser, isAuthenticated } = useAuth();
  const location = useLocation();
  const content = (propertyData[language] || propertyData.en).contact;
  const authText = authTranslations[language] || authTranslations.en;
  
  const [formData, setFormData] = useState({
    phone: '',
    message: '',
    // Honeypot fields - hidden from users, bots will fill them
    website: '',
    company: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formLoadedAt, setFormLoadedAt] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const recaptchaRef = useRef(null);

  // Record when form becomes available (for timing check)
  useEffect(() => {
    if (isAuthenticated && !formLoadedAt) {
      setFormLoadedAt(Date.now() / 1000); // Unix timestamp in seconds
    }
  }, [isAuthenticated, formLoadedAt]);

  // Scroll to contact section after auth redirect
  useEffect(() => {
    const shouldScroll = sessionStorage.getItem('scrollToContact');
    if (shouldScroll) {
      sessionStorage.removeItem('scrollToContact');
      // Use instant scroll for immediate positioning
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'instant' });
      }
    }
  }, []);

  // Handle user data passed from AuthCallback
  useEffect(() => {
    if (location.state?.user) {
      updateUser(location.state.user);
      // Clear the state to prevent re-setting on re-renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state, updateUser]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error(authText.signInPrompt);
      return;
    }

    if (!formData.message || formData.message.trim().length < 10) {
      toast.error(language === 'en' ? 'Please enter a message (at least 10 characters)' : content.error);
      return;
    }

    if (!recaptchaToken) {
      toast.error(language === 'en' ? 'Please complete the "I\'m not a robot" verification' : 'Παρακαλώ ολοκληρώστε την επαλήθευση');
      return;
    }

    if (!privacyConsent) {
      toast.error(authText.privacyRequired);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: user.name,
          email: user.email,
          phone: formData.phone || null,
          message: formData.message,
          interest: 'general',
          // Honeypot fields - should be empty
          website: formData.website || null,
          company: formData.company || null,
          // Timing check
          form_loaded_at: formLoadedAt,
          // reCAPTCHA token
          recaptcha_token: recaptchaToken,
          // Privacy consent
          privacy_consent: privacyConsent
        }),
      });

      if (response.status === 401) {
        toast.error(authText.signInPrompt);
        return;
      }

      if (response.status === 429) {
        // Rate limit error
        const data = await response.json();
        toast.error(data.detail || (language === 'en' ? 'Too many requests. Please try again later.' : 'Πολλές αιτήσεις. Δοκιμάστε αργότερα.'));
        return;
      }

      if (response.status === 400) {
        // Spam or validation error
        const data = await response.json();
        toast.error(data.detail || (language === 'en' ? 'Invalid submission. Please check your message.' : 'Μη έγκυρη υποβολή.'));
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      // Track successful contact form submission
      trackContactSubmit('google_auth');
      
      toast.success(content.success);
      setFormData({ phone: '', message: '', website: '', company: '' });
      setFormLoadedAt(Date.now() / 1000); // Reset timer for next submission
      setRecaptchaToken(null);
      setPrivacyConsent(false); // Reset privacy consent
      // Reset reCAPTCHA widget
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(language === 'en' ? 'Failed to send inquiry. Please try again.' : 'Αποτυχία αποστολής. Παρακαλώ δοκιμάστε ξανά.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-[#a8cef1] to-[#7eb3e3] relative overflow-hidden" aria-labelledby="contact-heading">
      {/* Floating decorative elements */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-float"></div>
      <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl animate-float" style={{ animationDelay: '1s' }}></div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <h2 className="text-4xl md:text-5xl font-light text-white mb-4 text-center" id="contact-heading">
          {content.title}
        </h2>
        <p className="text-lg text-white/90 mb-12 text-center">
          {content.subtitle}
        </p>

        <Card className="shadow-2xl border-none backdrop-blur-xl bg-white/95 transform hover:scale-[1.02] transition-all duration-500">
          <CardContent className="p-8">
            {/* Auth Status Section */}
            {loading ? (
              <div className="flex items-center justify-center py-4 mb-6">
                <div className="w-6 h-6 border-2 border-[#d4845f] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : isAuthenticated ? (
              <div className="flex items-center justify-between p-4 mb-6 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-3">
                  {user.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="w-10 h-10 rounded-full border-2 border-emerald-400"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-emerald-400 flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm text-emerald-600 font-medium">{authText.signedInAs}</span>
                    </div>
                    <p className="font-semibold text-slate-800">{user.name}</p>
                    <p className="text-sm text-slate-500">{user.email}</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="text-slate-500 hover:text-slate-700"
                  data-testid="contact-logout-button"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {authText.signOut}
                </Button>
              </div>
            ) : (
              <div className="text-center p-6 mb-6 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                <p className="text-slate-600 mb-4">{authText.signInPrompt}</p>
                <Button
                  type="button"
                  onClick={loginWithGoogle}
                  className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 shadow-md hover:shadow-lg transition-all duration-300"
                  data-testid="contact-google-login-button"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {authText.signInButton}
                </Button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot fields - hidden from users, bots will fill these */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {/* Show user info (read-only) when authenticated */}
              {isAuthenticated && (
                <>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {content.form.name}
                      </label>
                      <Input
                        type="text"
                        value={user?.name || ''}
                        disabled
                        className="w-full border-slate-300 bg-slate-50 text-slate-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {content.form.email}
                      </label>
                      <Input
                        type="email"
                        value={user?.email || ''}
                        disabled
                        className="w-full border-slate-300 bg-slate-50 text-slate-600"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="transform transition-all duration-300 hover:translate-x-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {authText.phoneOptional}
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!isAuthenticated}
                  className="w-full border-slate-300 focus:border-[#d4845f] focus:ring-[#d4845f] transition-all duration-300 disabled:bg-slate-100 disabled:cursor-not-allowed"
                />
              </div>

              <div className="transform transition-all duration-300 hover:translate-x-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {authText.yourMessage} *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  disabled={!isAuthenticated}
                  minLength={10}
                  className="w-full border-slate-300 focus:border-[#d4845f] focus:ring-[#d4845f] resize-none transition-all duration-300 disabled:bg-slate-100 disabled:cursor-not-allowed"
                  required
                />
                {/* Character countdown */}
                <div className="mt-2 text-sm">
                  {formData.message.length < 10 ? (
                    <span className="text-amber-600 font-medium">
                      {10 - formData.message.length} {language === 'en' ? 'more characters needed' : 'χαρακτήρες ακόμα'}
                    </span>
                  ) : (
                    <span className="text-green-600 font-medium flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      {formData.message.length} {language === 'en' ? 'characters' : 'χαρακτήρες'}
                    </span>
                  )}
                </div>
              </div>

              {/* reCAPTCHA widget */}
              {isAuthenticated && RECAPTCHA_SITE_KEY && (
                <div className="flex justify-center transform transition-all duration-300">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                    onExpired={handleRecaptchaExpired}
                    theme="light"
                  />
                </div>
              )}

              {/* Privacy Policy Consent */}
              {isAuthenticated && (
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <input
                    type="checkbox"
                    id="privacy-consent"
                    checked={privacyConsent}
                    onChange={(e) => setPrivacyConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-[#d4845f] focus:ring-[#d4845f]"
                    data-testid="privacy-consent-checkbox"
                  />
                  <label htmlFor="privacy-consent" className="text-sm text-slate-600">
                    {authText.privacyConsent}{' '}
                    <Link 
                      to="/privacy" 
                      target="_blank"
                      className="text-[#d4845f] hover:underline font-medium"
                    >
                      {authText.privacyPolicy}
                    </Link>
                    {' *'}
                  </label>
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting || !isAuthenticated || !recaptchaToken || !privacyConsent}
                data-testid="contact-submit-button"
                className="w-full bg-[#d4845f] hover:bg-[#c17552] text-white py-6 text-lg transition-all duration-300 hover:scale-105 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                style={{
                  boxShadow: isAuthenticated && recaptchaToken && privacyConsent ? '0 20px 60px rgba(212, 132, 95, 0.4)' : 'none'
                }}
              >
                {isSubmitting ? (language === 'en' ? 'Sending...' : 'Αποστολή...') : content.form.submit}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default Contact;
